import { useCallback, useEffect, useRef, useState } from 'react';

const SR = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

// ¿El navegador trae reconocimiento de voz? (Chrome/Android sí, con el mismo
// motor de Google que el dictado del teclado; Firefox no.)
export const vozSoportada = !!SR;

/**
 * Hook para Web Speech API (reconocimiento de voz).
 *
 * Estados:
 *   - 'idle'      — nada activo
 *   - 'listening' — micrófono activo escuchando (`parcial` trae lo que va
 *                   entendiendo, para mostrarlo en vivo como el panel de Google)
 *   - 'error'     — sin resultado; `error` dice por qué:
 *                   'no-speech' | 'not-allowed' | 'network' | 'otro'
 *
 * Retorna:
 *   { isSupported, state, transcript, parcial, error, start, stop, reset }
 *
 * Cada `start()` crea un reconocedor nuevo: reutilizar uno después de un
 * error deja a Chrome en Android sin escuchar y sin avisar.
 */
export function useVoiceSearch({ lang = 'es-MX' } = {}) {
  const [state, setState] = useState('idle');
  const [transcript, setTranscript] = useState(null);
  const [parcial, setParcial] = useState('');
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);

  const soltar = () => {
    const r = recognitionRef.current;
    if (!r) return;
    r.onresult = null;
    r.onerror = null;
    r.onend = null;
    try { r.abort(); } catch { /* ya terminó */ }
    recognitionRef.current = null;
  };

  useEffect(() => soltar, []);

  const start = useCallback(() => {
    if (!SR) return;
    soltar();
    setTranscript(null);
    setParcial('');
    setError(null);
    setState('listening');

    const r = new SR();
    r.lang = lang;
    r.continuous = false;
    r.interimResults = true;
    r.maxAlternatives = 1;

    let texto = '';
    let terminado = false;
    const terminar = (final) => {
      if (terminado) return;
      terminado = true;
      setTranscript(final);
      setState('idle');
    };

    r.onresult = (e) => {
      texto = Array.from(e.results).map((res) => res[0]?.transcript || '').join('').trim();
      setParcial(texto);
      const ultimo = e.results[e.results.length - 1];
      if (ultimo?.isFinal && texto) terminar(texto);
    };

    r.onerror = (e) => {
      if (terminado) return;
      // 'aborted' lo provoca nuestro propio stop/abort: no es un error.
      if (e.error === 'aborted') return;
      terminado = true;
      setError(
        e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'not-allowed'
          : e.error === 'network' ? 'network'
          : e.error === 'no-speech' || e.error === 'audio-capture' ? 'no-speech'
          : 'otro'
      );
      setState('error');
    };

    r.onend = () => {
      if (terminado) return;
      // Hay navegadores que cierran sin marcar el resultado como final: si ya
      // se entendió algo, eso es lo que dijo.
      if (texto) return terminar(texto);
      terminado = true;
      setError('no-speech');
      setState('error');
    };

    recognitionRef.current = r;
    try {
      r.start();
    } catch {
      setError('otro');
      setState('error');
    }
  }, [lang]);

  const stop = useCallback(() => {
    soltar();
    setState('idle');
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setTranscript(null);
    setParcial('');
    setError(null);
  }, []);

  return { isSupported: vozSoportada, state, transcript, parcial, error, start, stop, reset };
}
